// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */
import { mergeClasses } from 'util/commons';
import styles from './BookingScannedTable.module.scss';
import { useTranslation } from 'react-i18next';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import { useMemo } from 'react';

type Column<T> = {
  dataIndex: keyof T;
  title: string;
  render?: (value: T) => React.ReactNode;
  width?: React.CSSProperties['width'];
};

type BookingScannedTableProps<
  T extends {
    qrCode: string;
  }
> = {
  columns: Column<T>[];
  data?: Record<string, T>;
  checkedIds?: string[];
  setCheckedIds?: (ids: string[]) => void;
};

function BookingScannedTable<
  T extends {
    qrCode: string;
  }
>({
  data = {},
  checkedIds = [],
  setCheckedIds,
  columns,
}: BookingScannedTableProps<T>) {
  const { t } = useTranslation();

  const qrCodes = useMemo(
    () => Object.values(data).map(({ qrCode }) => qrCode),
    [data]
  );

  const handleCheckAll = () => {
    if (checkedIds.length === qrCodes.length) {
      setCheckedIds?.([]);
    } else {
      setCheckedIds?.(qrCodes);
    }
  };

  const handleCheck = (id: string) => {
    if (checkedIds.includes(id)) {
      setCheckedIds?.(checkedIds.filter((item) => item !== id));
    } else {
      setCheckedIds?.([...checkedIds, id]);
    }
  };

  const renderDataList = () => {
    if (!Object.keys(data).length) {
      return (
        <tr>
          <td colSpan={columns.length + 1} className={styles.noData}>
            {t('messages.M_008')}
          </td>
        </tr>
      );
    }

    return Object.values(data).map(({ qrCode }) => (
      <tr key={qrCode}>
        <td width={50}>
          <CustomCheckBox
            id={`scanned-${qrCode}`}
            checked={checkedIds.includes(qrCode)}
            onChange={() => handleCheck(qrCode)}
          />
        </td>

        {columns.map(({ dataIndex, render, width }, index) => (
          <td key={index} width={width}>
            {render ? render(data[qrCode]) : data[qrCode][dataIndex]}
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <table className={mergeClasses('primary-table', styles.scannedTable)}>
      <thead>
        <tr>
          <th
            style={{
              width: 50,
            }}
          >
            <CustomCheckBox
              id="scanned-checkAll"
              checked={
                qrCodes.length > 0 && checkedIds.length === qrCodes.length
              }
              onChange={handleCheckAll}
              disabled={!qrCodes.length}
            />
          </th>

          {columns.map(({ title, width }, index) => (
            <th key={index} style={{ width }}>
              {title}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>{renderDataList()}</tbody>
    </table>
  );
}

export default BookingScannedTable;
